import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { CustomShortCodeModel } from '@domain-group/realhub-backend-api-types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/customShortcode/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/customShortcode/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/customShortcode/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/customShortcode/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/customShortcode/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/customShortcode/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/customShortcode/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/customShortcode/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/customShortcode/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/customShortcode/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/customShortcode/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/customShortcode/DELETE_FAILURE'

export type CustomShortcodeModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: CustomShortcodeModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCustomShortcodes(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<CustomShortCodeModel[]>> => {
    const loadedForKeys = [...getState().customShortcodes.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/custom_shortcodes.json', options)
      .then(({ data }: { data: CustomShortCodeModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CUSTOM_SHORTCODE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCustomShortcode(customShortcode: Partial<CustomShortCodeModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      custom_shortcode: customShortcode,
    }),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<CustomShortCodeModel>> => {
    dispatch(createRequest())

    const promise = api('/custom_shortcodes.json', options, config)
      .then(({ data }: { data: CustomShortCodeModel }) => {
        const normalizedJson = normalize(data, Schemas.CUSTOM_SHORTCODE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCustomShortcode(
  customShortcode: UpdateParams<CustomShortCodeModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      custom_shortcode: customShortcode,
    }),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<CustomShortCodeModel>> => {
    dispatch(updateRequest())

    const promise = api(`/custom_shortcodes/${customShortcode.id}.json`, options, config)
      .then(({ data }: { data: CustomShortCodeModel }) => {
        const normalizedJson = normalize(data, Schemas.CUSTOM_SHORTCODE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteCustomShortcode(customShortcode: DeleteParams<CustomShortCodeModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch): Promise<ActionResult<{ id: number }>> => {
    dispatch(deleteRequest())

    const promise = api(`/custom_shortcodes/${customShortcode.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.CUSTOM_SHORTCODE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CustomShortcodeModuleState = initialState,
  action: Action = { type: '' },
): CustomShortcodeModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
